import { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import { PickupTimeSlot, CreatePickupTimeSlotRequest, UpdatePickupTimeSlotRequest } from '../types';
import { useAuth } from '../hooks/useAuth';

interface FormState {
  label: string;
  displayOrder: number;
  isActive: boolean;
}

const EMPTY_FORM: FormState = { label: '', displayOrder: 0, isActive: true };

export default function PickupTimeSlotsPage() {
  const auth = useAuth();
  const [slots, setSlots] = useState<PickupTimeSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [includeInactive, setIncludeInactive] = useState(false);

  // Modal state: null = closed, 'new' = create, string = editing that id
  const [modalMode, setModalMode] = useState<'new' | string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const fetchSlots = useCallback(() => {
    if (!auth.token) return;
    setLoading(true);
    setError(null);
    api
      .listPickupTimeSlots(includeInactive, auth.token)
      .then(setSlots)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [auth.token, includeInactive]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  const openCreate = () => {
    setModalMode('new');
    setForm(EMPTY_FORM);
    setSaveError(null);
  };

  const openEdit = (slot: PickupTimeSlot) => {
    setModalMode(slot.id);
    setForm({ label: slot.label, displayOrder: slot.displayOrder, isActive: slot.isActive });
    setSaveError(null);
  };

  const closeModal = () => {
    setModalMode(null);
    setForm(EMPTY_FORM);
    setSaveError(null);
  };

  const submit = async () => {
    if (!auth.token) return;
    if (!form.label.trim()) {
      setSaveError('Label is required');
      return;
    }
    if (form.displayOrder < 0) {
      setSaveError('Display order must be 0 or greater');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      if (modalMode === 'new') {
        const body: CreatePickupTimeSlotRequest = { label: form.label.trim(), displayOrder: form.displayOrder };
        await api.createPickupTimeSlot(body, auth.token);
      } else if (modalMode) {
        const body: UpdatePickupTimeSlotRequest = {
          label: form.label.trim(),
          displayOrder: form.displayOrder,
          isActive: form.isActive,
        };
        await api.updatePickupTimeSlot(modalMode, body, auth.token);
      }
      closeModal();
      fetchSlots();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (slot: PickupTimeSlot) => {
    if (!auth.token) return;
    if (!confirm(`Delete pickup time slot "${slot.label}"?\n\nIf any active order references this slot, the delete will fail with 422 — deactivate it instead.`)) {
      return;
    }
    try {
      await api.deletePickupTimeSlot(slot.id, auth.token);
      fetchSlots();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Delete failed';
      alert(`Could not delete: ${msg}\n\nTip: open the edit modal and toggle 'Active' off instead — that hides it from customers without breaking existing orders.`);
    }
  };

  if (loading) return <div className="loading">Loading…</div>;
  if (error) return <div className="error-banner">{error}</div>;

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1>Pickup Time Slots</h1>
          <p className="page-subtitle">
            Admin-global list of pickup time slots. Customers pick from active slots at checkout.
          </p>
        </div>
        <button className="btn-primary" onClick={openCreate}>+ Create slot</button>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <input
          type="checkbox"
          checked={includeInactive}
          onChange={(e) => setIncludeInactive(e.target.checked)}
        />
        Show inactive slots
      </label>

      <table className="admin-table">
        <thead>
          <tr>
            <th>Label</th>
            <th>Display order</th>
            <th>Active</th>
            <th>Last edited</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {slots.length === 0 && (
            <tr><td colSpan={5} style={{ textAlign: 'center', padding: 24 }}>No pickup time slots yet — click "+ Create slot" to add one.</td></tr>
          )}
          {slots.map((slot) => (
            <tr key={slot.id}>
              <td>{slot.label}</td>
              <td>{slot.displayOrder}</td>
              <td>{slot.isActive ? '✓ Active' : '— Inactive'}</td>
              <td>{new Date(slot.updatedAt).toLocaleString()}</td>
              <td style={{ textAlign: 'right' }}>
                <button className="btn-secondary" onClick={() => openEdit(slot)}>Edit</button>
                <button className="btn-danger" style={{ marginLeft: 8 }} onClick={() => remove(slot)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {modalMode && (
        <div className="modal-backdrop" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{modalMode === 'new' ? 'Create pickup time slot' : 'Edit pickup time slot'}</h2>

            <label className="form-field">
              <span>Label</span>
              <input
                type="text"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="e.g. Morning (08:00 - 12:00)"
                maxLength={100}
              />
            </label>

            <label className="form-field">
              <span>Display order</span>
              <input
                type="number"
                min={0}
                value={form.displayOrder}
                onChange={(e) => setForm({ ...form, displayOrder: parseInt(e.target.value, 10) || 0 })}
              />
              <small>Smaller numbers appear first in the customer picker.</small>
            </label>

            {modalMode !== 'new' && (
              <label className="form-field" style={{ display: 'flex', alignItems: 'center', gap: 8, flexDirection: 'row' }}>
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
                <span>Active (uncheck to hide from customers without affecting existing bookings)</span>
              </label>
            )}

            {saveError && <div className="error-banner">{saveError}</div>}

            <div className="modal-actions">
              <button className="btn-secondary" onClick={closeModal} disabled={saving}>Cancel</button>
              <button className="btn-primary" onClick={submit} disabled={saving}>
                {saving ? 'Saving…' : modalMode === 'new' ? 'Create' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
